import { useQuery } from "@tanstack/react-query";
import { queryStorefrontApi } from "./storefront";

// Global metafield query for store-level metafields
// To add more metafields, add them to the identifiers array:
// identifiers: [
//   {namespace: "custom", key: "free_shipping_bundle_product_ids"},
//   {namespace: "custom", key: "another_metafield"},
//   {namespace: "global", key: "store_config"}
// ]
const GLOBAL_METAFIELD_QUERY = `
  query getGlobalMetafields {
    shop {
      metafields(identifiers: [
        {namespace: "custom", key: "free_shipping_bundle_product_ids"}
      ]) {
        key
        namespace
        value
        type
      }
    }
  }
`;

type GlobalMetafield = {
  namespace: string;
  key: string;
  value: string;
  type: string;
};

type GlobalMetafieldsResponse = {
  data: {
    shop: {
      metafields: GlobalMetafield[];
    };
  };
};

// Cache key for global metafields
export const globalMetafieldsApiKeys = {
  all: ["global-metafields"] as const,
};

// Fetch global metafields
const fetchGlobalMetafields = async (): Promise<GlobalMetafield[]> => {
  const response = await queryStorefrontApi<GlobalMetafieldsResponse>(
    GLOBAL_METAFIELD_QUERY
  );
  return response.data.shop.metafields;
};

// Hook to get global metafields
export const useGlobalMetafields = () => {
  return useQuery({
    queryKey: globalMetafieldsApiKeys.all,
    queryFn: fetchGlobalMetafields,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Helper function to get a specific metafield value
export const getGlobalMetafieldValue = (
  metafields: GlobalMetafield[],
  namespace: string,
  key: string
): string | null => {
  const metafield = metafields.find(
    (mf) => mf && mf.namespace === namespace && mf.key === key
  );
  return metafield?.value || null;
};

// Helper function to get free shipping bundle product IDs
export const getFreeShippingBundleProductIds = (
  metafields: GlobalMetafield[]
): number[] => {
  const value = getGlobalMetafieldValue(
    metafields,
    "custom",
    "free_shipping_bundle_product_ids"
  );

  // If metafield is empty or not set, return empty array (no free shipping)
  if (!value || value.trim() === "") {
    return [];
  }

  try {
    const parsedValue = JSON.parse(value);
    // If parsed value is not an array or is empty, return empty array
    if (!Array.isArray(parsedValue) || parsedValue.length === 0) {
      return [];
    }
    return parsedValue;
  } catch (error) {
    console.error("Failed to parse free shipping bundle product IDs:", error);
    // If parsing fails, return empty array (no free shipping)
    return [];
  }
};

// Helper function to get any JSON metafield value
export const getGlobalMetafieldJsonValue = <T = any>(
  metafields: GlobalMetafield[],
  namespace: string,
  key: string,
  fallback?: T
): T | null => {
  const value = getGlobalMetafieldValue(metafields, namespace, key);

  if (!value) {
    return fallback || null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.error(`Failed to parse metafield ${namespace}.${key}:`, error);
    return fallback || null;
  }
};

// Helper function to get a string metafield value
export const getGlobalMetafieldStringValue = (
  metafields: GlobalMetafield[],
  namespace: string,
  key: string,
  fallback?: string
): string | null => {
  const value = getGlobalMetafieldValue(metafields, namespace, key);
  return value || fallback || null;
};
